import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Popover from '@mui/material/Popover'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import BusinessIcon from '@mui/icons-material/Business'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import { ReactComponent as PortfolioIcon } from 'assets/images/portfolio.svg'
import { getUserBps } from 'features/bpList/ducks/selectors'
import { fetchUserBpsRoutine } from 'features/bpList/ducks/actions'
import { selectPortfolioList, selectPortfolioLoading } from 'ducks/portfolio/selectors'
import { listPortfoliosRoutine } from 'ducks/portfolio/actions'
import { selectEstates } from 'ducks/estates/selectors'
import { listEstatesRoutine } from 'ducks/estates/actions'

const PickerBody = styled.div`
  width: 360px;
  max-width: 90vw;
  max-height: 70vh;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100vw;
    max-width: 100vw;
    max-height: 80vh;
  }
`

const SearchWrap = styled.div`
  padding: 12px 14px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const Sections = styled.div`
  overflow-y: auto;
  padding: 6px 0 10px;
`

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.grey[200]};
  padding: 10px 16px 4px;
`

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: ${({ $active }) => ($active ? 'rgba(0,0,0,0.04)' : 'transparent')};
  border: none;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  text-align: left;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`

const RowIcon = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const RowLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Empty = styled.div`
  padding: 14px 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const normaliseSearch = text => (text || '').trim().toLowerCase()

const matches = (haystack, needle) => {
  if (!needle) return true
  return (haystack || '').toLowerCase().includes(needle)
}

const GuruScopePicker = ({ anchorEl, open, onClose, currentScope, onSelect }) => {
  const dispatch = useDispatch()
  const bps = useSelector(getUserBps)
  const portfolios = useSelector(selectPortfolioList)
  const portfoliosLoading = useSelector(selectPortfolioLoading)
  const estates = useSelector(selectEstates)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!open) return
    if (!Array.isArray(bps) || bps.length === 0) {
      dispatch(fetchUserBpsRoutine({ page: 1 }))
    }
    if (!Array.isArray(portfolios) || portfolios.length === 0) {
      dispatch(listPortfoliosRoutine())
    }
    if (!Array.isArray(estates) || estates.length === 0) {
      dispatch(listEstatesRoutine({ page: 1 }))
    }
  }, [open, dispatch])

  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  const needle = normaliseSearch(search)

  const filteredBps = useMemo(() => {
    const list = Array.isArray(bps) ? bps : []
    return list.filter(bp => matches(bp?.name, needle) || matches(bp?.address, needle))
  }, [bps, needle])

  const filteredPortfolios = useMemo(() => {
    const list = Array.isArray(portfolios) ? portfolios : []
    return list.filter(p => matches(p?.name, needle))
  }, [portfolios, needle])

  const globalMatches = matches('all my building passports global', needle)

  const currentEstateId = currentScope?.contextType === 'estate' ? currentScope.contextId : null

  const currentEstate = useMemo(() => {
    if (!currentEstateId) return null
    const list = Array.isArray(estates) ? estates : []
    return list.find(e => e?.id === currentEstateId) || null
  }, [estates, currentEstateId])

  const estateRowLabel = currentEstate?.name || 'This estate'
  const estateMatches = !!currentEstateId && matches(estateRowLabel, needle)

  const isActive = (type, id) =>
    currentScope?.contextType === type && (currentScope?.contextId || null) === (id || null)

  const handlePick = scope => {
    onSelect(scope)
    onClose()
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <PickerBody>
        <SearchWrap>
          <TextField
            fullWidth
            size="small"
            placeholder="Search Building Passports or portfolios"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </SearchWrap>
        <Sections>
          {globalMatches && (
            <>
              <SectionTitle>Global</SectionTitle>
              <Row
                $active={isActive('user', null)}
                onClick={() => handlePick({ contextType: 'user', contextId: null, contextLabel: null })}
              >
                <RowIcon><AutoAwesomeOutlinedIcon fontSize="small" /></RowIcon>
                <RowLabel>All my Building Passports</RowLabel>
              </Row>
            </>
          )}
          {filteredBps.length > 0 && (
            <>
              <SectionTitle>Building Passports</SectionTitle>
              {filteredBps.map(bp => (
                <Row
                  key={bp.id}
                  $active={isActive('bp', bp.id)}
                  onClick={() => handlePick({
                    contextType: 'bp',
                    contextId: bp.id,
                    contextLabel: bp.name || bp.address || 'Building Passport',
                  })}
                >
                  <RowIcon><BusinessIcon fontSize="small" /></RowIcon>
                  <RowLabel>{bp.name || bp.address || 'Building Passport'}</RowLabel>
                </Row>
              ))}
            </>
          )}
          <SectionTitle>Portfolios</SectionTitle>
          {filteredPortfolios.length > 0 ? (
            filteredPortfolios.map(p => (
              <Row
                key={p.id}
                $active={isActive('portfolio', p.id)}
                onClick={() => handlePick({
                  contextType: 'portfolio',
                  contextId: p.id,
                  contextLabel: p.name || 'Portfolio',
                })}
              >
                <RowIcon><PortfolioIcon style={{ width: 18, height: 18 }} /></RowIcon>
                <RowLabel>{p.name || 'Portfolio'}</RowLabel>
              </Row>
            ))
          ) : portfoliosLoading ? (
            <Empty><CircularProgress size={14} /> &nbsp;Loading portfolios&hellip;</Empty>
          ) : needle ? (
            <Empty>No portfolios match &ldquo;{search}&rdquo;.</Empty>
          ) : (
            <Empty>You don&rsquo;t belong to any portfolios yet.</Empty>
          )}
          {estateMatches && (
            <>
              <SectionTitle>This estate</SectionTitle>
              <Row
                $active={isActive('estate', currentEstateId)}
                onClick={() => handlePick({
                  contextType: 'estate',
                  contextId: currentEstateId,
                  contextLabel: currentEstate?.name || null,
                })}
              >
                <RowIcon><AccountTreeOutlinedIcon fontSize="small" /></RowIcon>
                <RowLabel>{estateRowLabel}</RowLabel>
              </Row>
            </>
          )}
        </Sections>
      </PickerBody>
    </Popover>
  )
}

export default GuruScopePicker
